/**
 * Pure model types for the quote carousel feature. Every later frontend task
 * (UI, hooks, GraphQL wiring) imports these types/helpers — keep names and
 * shapes stable.
 */

export type QuoteSlideStyle = 'card' | 'frame';

export type QuoteCarouselFormat = 'portrait_4x5' | 'portrait_9x16';

/** Which video a frame background is grabbed from: `original` = the
 *  project's/library's full source video (source axis), `edited` = the
 *  rendered export (`finished_video`, subtitles/layout/layers burned in). */
export type QuoteFrameSource = 'original' | 'edited';

export interface QuoteFrameBackground {
  kind: 'frame';
  /** Uploaded https still; null = not captured yet. */
  url: string | null;
  source: QuoteFrameSource;
  /** null = automatic (the frame at the quote's moment). A number = a
   *  user-picked time in seconds on the chosen video's OWN timeline: the
   *  SOURCE-video axis for `original`, the export axis for `edited`. */
  time: number | null;
}

export type QuoteSlideBackground =
  | QuoteFrameBackground
  | { kind: 'color'; color: string }; // #RRGGBB

/** Center of the text block, fractions (0..1) of the slide. */
export interface QuoteTextPosition {
  x: number;
  y: number;
}

/** Pan/zoom of a slide's frame background — the video-layer reframe
 *  semantics (`computeReframeTransform`): scale 1–3, offsets −1..1. */
export interface QuoteBackgroundFrame {
  scale: number;
  offsetX: number;
  offsetY: number;
}

export interface QuoteSlide {
  id: string;
  text: string;
  start: number;
  end: number;
  background: QuoteSlideBackground | null;
  /** Absent = the style's default layout. */
  textPosition?: QuoteTextPosition;
  /** Absent = cover-fit, centered. */
  backgroundFrame?: QuoteBackgroundFrame;
}

/** Carousel-wide text look, overriding the template. Every field is
 *  optional: absent = the template / style default. */
export interface QuoteTextStyle {
  /** CSS family or backend font id (FontSelector value). */
  fontFamily?: string;
  /** Font file URL persisted with the id (text-layer rule: render bundles
   *  cannot resolve catalog ids). https only. */
  fontUrl?: string | null;
  textColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  /** 0.7–1.3, multiplies the auto-fit maximum font size. */
  sizeScale?: number;
  align?: 'left' | 'center' | 'right';
  outline?: { enabled: boolean; color: string; widthPct: number };
  bold?: boolean;
  italic?: boolean;
}

export interface QuoteCarousel {
  version: 1;
  style: QuoteSlideStyle;
  format: QuoteCarouselFormat;
  /** Source for automatic frame backgrounds (new slides and the
   *  carousel-wide switch). Hand-picked frames keep their own source. */
  frameSource: QuoteFrameSource;
  brandKitId: string | null;
  handle: string;
  slides: QuoteSlide[];
  /** Absent = the template's look. */
  textStyle?: QuoteTextStyle;
}

export const QUOTE_MAX_CHARS = 220;
export const MIN_SLIDES = 2;
export const MAX_SLIDES = 10;
